import React, { useEffect, useState } from "react";
import {
  Card,
  Typography,
  Divider,
  Select,
  Form,
  Input,
  FormInstance,
  Button,
  Space,
} from "antd";
import "./OrderCard.css";
import axios from "axios";

interface Area {
  Ref: string;
  Description: string;
}
interface AreaOption {
  value: string;
  label: string;
}
interface Village {
  Description: string;
  Ref: string;
}
interface VillageOpt {
  label: string;
  value: string;
}
interface DeliveryPointRes {
  Description: string;
}
interface DeliveryPoint {
  label: string;
  value: string;
}
interface FormValues {
  pib: string;
  email: string;
  phone: number;
  area: string;
  village: string;
  deliveryPoint: string;
}
interface Item {
  Image: string[];
  Price: number;
  Name: string;
  Desc: string;
}

interface OrderCardProps {
  item: Item | null;
  sendEmail: (values: FormValues) => void;
  sending?: boolean;
  quantity: number;
  total: number;
}

const moneyUAH = (v: number) => `${Math.round(v).toLocaleString("uk-UA")} ₴`;

const OrderCard: React.FC<OrderCardProps> = ({ item, sendEmail, sending, quantity, total }) => {
  const [form] = Form.useForm();

  const [areas, setAreas] = useState<Area[]>([]);
  const [areaOptions, setAreaOptions] = useState<AreaOption[]>([]);
  const [selectedArea, setSelectedArea] = useState<string | null>(null);

  const [villages, setVillages] = useState<Village[]>([]);
  const [villageOpt, setVillageOpt] = useState<VillageOpt[]>([]);
  const [village, setVillage] = useState<string>("");

  const [deliveryOpt, setDeliveryOpt] = useState<DeliveryPoint[]>([]);

  useEffect(() => {
    const getAreas = async () => {
      const requestData = {
        apiKey: "b16d22f3f7dee2ecbe55400f7096f12c",
        modelName: "Address",
        calledMethod: "getAreas",
        methodProperties: {},
      };
      const res = await axios.post("https://api.novaposhta.ua/v2.0/json/", requestData);
      if (res.data?.success) setAreas(res.data.data);
    };
    getAreas();
  }, []);

  useEffect(() => {
    setAreaOptions(areas.map((a) => ({ label: a.Description, value: a.Description })));
  }, [areas]);

  const handleArea = async (value: string) => {
    setSelectedArea(value);

    // скинути залежні поля
    form.setFieldsValue({ village: undefined, deliveryPoint: undefined });
    setVillage("");
    setDeliveryOpt([]);
    setVillageOpt([]);

    const requestData = {
      apiKey: "b16d22f3f7dee2ecbe55400f7096f12c",
      modelName: "Address",
      calledMethod: "getCities",
      methodProperties: {
        AreaRef: areas.filter((i) => i.Description === value)[0].Ref,
      },
    };

    const res = await axios.post("https://api.novaposhta.ua/v2.0/json/", requestData);
    if (res.data?.success) {
      const data: Village[] = res.data.data;
      setVillageOpt(data.map((i) => ({ label: i.Description, value: i.Description })));
      setVillages(data.map((i) => ({ Description: i.Description, Ref: i.Ref })));
    }
  };

  const handleVillage = async (value: string) => {
    setVillage(value);

    // скинути відділення
    form.setFieldsValue({ deliveryPoint: undefined });
    setDeliveryOpt([]);

    const requestData = {
      apiKey: "b16d22f3f7dee2ecbe55400f7096f12c",
      modelName: "Address",
      calledMethod: "getWarehouses",
      methodProperties: {
        CityRef: villages.filter((i) => i.Description === value)[0]?.Ref,
      },
    };

    const res = await axios.post("https://api.novaposhta.ua/v2.0/json/", requestData);
    if (res.data?.success) {
      const data: DeliveryPointRes[] = res.data.data;
      setDeliveryOpt(data.map((i) => ({ label: i.Description, value: i.Description })));
    }
  };

  const SubmitButton = ({ form }: { form: FormInstance }) => {
    const [submittable, setSubmittable] = React.useState(false);
    const values = Form.useWatch([], form);

    React.useEffect(() => {
      form.validateFields({ validateOnly: true }).then(
        () => setSubmittable(true),
        () => setSubmittable(false)
      );
    }, [values]);

    return (
      <Button
        type="primary"
        htmlType="submit"
        disabled={!submittable}
        loading={!!sending}
        style={{ width: "100%", borderRadius: 12, height: 40 }}
      >
        Оформити замовлення
      </Button>
    );
  };

  const onSubmit = (values: FormValues) => {
    sendEmail(values);
  };

  return (
    <Card className="pro-card" styles={{ body: { padding: 16 } }}>
      <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Деталі доставки
        </Typography.Title>
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/7/72/Nova_Poshta_2021_logo.png"
          height={18}
          alt="Nova Poshta"
        />
      </Space>

      <Divider style={{ margin: "12px 0" }} />

      {/* Підсумок замовлення */}
      <div className="order-summary">
        <div className="order-summary__row">
          <span className="muted">Товар</span>
          <span className="strong">{item?.Name}</span>
        </div>
        <div className="order-summary__row">
          <span className="muted">Кількість</span>
          <span className="strong">{quantity}</span>
        </div>
        <div className="order-summary__row">
          <span className="muted">Сума</span>
          <span className="strong">{moneyUAH(total)}</span>
        </div>
      </div>

      <Divider style={{ margin: "12px 0" }} />

      <Form form={form} name="validateOnly" layout="vertical" autoComplete="off" onFinish={onSubmit}>
        <Form.Item
          name="pib"
          label="ПІБ"
          rules={[{ required: true, message: "ПІБ обов'язкове для відправки" }]}
        >
          <Input placeholder="Наприклад: Іваненко Іван Іванович" />
        </Form.Item>

        <Form.Item
          name="phone"
          label="Номер телефону"
          rules={[{ required: true, message: "Номер обов'язковий для відправки" }]}
        >
          <Input placeholder="+380..." />
        </Form.Item>

        <Form.Item
          name="email"
          label="Email"
          rules={[{ type: "email", required: false, message: "Невірно вказаний Email" }]}
        >
          <Input placeholder="Необов’язково" />
        </Form.Item>

        <Form.Item name="area" rules={[{ required: true, message: "Оберіть область" }]}>
          <Select
            showSearch
            className="field"
            placeholder="Оберіть область..."
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={areaOptions}
            onChange={handleArea}
          />
        </Form.Item>

        <Form.Item name="village" rules={[{ required: true, message: "Оберіть населений пункт" }]}>
          <Select
            showSearch
            className="field"
            placeholder="Оберіть населений пункт..."
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={villageOpt}
            onChange={handleVillage}
            disabled={!selectedArea}
          />
        </Form.Item>

        <Form.Item name="deliveryPoint" rules={[{ required: true, message: "Оберіть відділення" }]}>
          <Select
            showSearch
            className="field"
            placeholder="Оберіть відділення..."
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={deliveryOpt}
            disabled={!village}
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0 }}>
          <SubmitButton form={form} />
        </Form.Item>

        <div className="order-hint">
          Натискаючи кнопку, ви підтверджуєте передачу даних для опрацювання замовлення.
        </div>
      </Form>
    </Card>
  );
};

export default OrderCard;
